import { renderCodexTab } from './codexTabRenderer';
import type { CodexTab, CodexTabDefinition } from './codexShared';

interface CodexPanelOptions {
  contentArea: HTMLElement;
  initialTab: CodexTab;
  getTabs: () => CodexTabDefinition[];
  onActiveTabChange?: (tab: CodexTab) => void;
}

export class CodexPanel {
  private static readonly SEARCH_DEBOUNCE_MS = 150;
  private static readonly LIST_SCROLL_SELECTOR = '.codex-list-scroll-root';
  private readonly contentArea: HTMLElement;
  private readonly getTabs: () => CodexTabDefinition[];
  private readonly onActiveTabChange?: (tab: CodexTab) => void;
  private readonly tabBar: HTMLElement;
  private readonly tabButtons = new Map<CodexTab, HTMLButtonElement>();
  private readonly tabRoots = new Map<CodexTab, HTMLElement>();
  private readonly searchQueries = new Map<CodexTab, string>();
  private readonly scrollTops = new Map<CodexTab, number>();
  private searchRenderTimer: number | null = null;
  private tabKeys = '';
  private activeTab: CodexTab;

  constructor(options: CodexPanelOptions) {
    this.contentArea = options.contentArea;
    this.getTabs = options.getTabs;
    this.onActiveTabChange = options.onActiveTabChange;
    this.activeTab = options.initialTab;
    this.tabBar = document.createElement('div');
    this.tabBar.className = 'char-tab-bar';
    this.syncTabButtons(this.getTabs());
  }

  public getTabBarElement(): HTMLElement {
    return this.tabBar;
  }

  public getActiveTab(): CodexTab {
    return this.activeTab;
  }

  public hasTab(tab: CodexTab): boolean {
    return this.getTabs().some((definition) => definition.key === tab);
  }

  public setActiveTab(tab: CodexTab): void {
    if (!this.hasTab(tab)) return;
    this.activeTab = tab;
    this.updateTabStyles();
    this.onActiveTabChange?.(tab);
    this.render();
  }

  public render(): void {
    this.captureActiveTabScrollTop();

    const activeElement = document.activeElement;
    const shouldRestoreSearchFocus =
      activeElement instanceof HTMLInputElement &&
      activeElement.classList.contains('codex-search-input') &&
      this.contentArea.contains(activeElement);
    const selectionStart = shouldRestoreSearchFocus ? activeElement.selectionStart : null;
    const selectionEnd = shouldRestoreSearchFocus ? activeElement.selectionEnd : null;

    const tabs = this.getTabs();
    this.syncTabButtons(tabs);
    for (const tab of tabs) {
      const root = this.ensureTabRoot(tab.key);
      root.style.display = this.activeTab === tab.key ? 'block' : 'none';
      if (this.activeTab === tab.key) {
        renderCodexTab(root, tab.getRows(), tab.emptyLabel, {
          query: this.searchQueries.get(tab.key) ?? '',
          onQueryInput: (value) => this.handleSearchInput(tab.key, value),
        });
      }
    }

    if (shouldRestoreSearchFocus) {
      const activeRoot = this.tabRoots.get(this.activeTab) ?? null;
      const nextInput = activeRoot?.querySelector<HTMLInputElement>('.codex-search-input') ?? null;
      if (nextInput) {
        nextInput.focus();
        if (selectionStart !== null && selectionEnd !== null) {
          nextInput.setSelectionRange(selectionStart, selectionEnd);
        }
      }
    }

    this.restoreActiveTabScrollTop();
  }

  public destroy(): void {
    if (this.searchRenderTimer !== null) {
      window.clearTimeout(this.searchRenderTimer);
      this.searchRenderTimer = null;
    }
    this.tabBar.remove();
  }

  private syncTabButtons(tabs: CodexTabDefinition[]): void {
    const nextTabKeys = tabs.map((tab) => tab.key).join('|');
    if (nextTabKeys === this.tabKeys) {
      this.updateTabStyles();
      return;
    }
    this.tabKeys = nextTabKeys;
    this.tabBar.innerHTML = '';
    this.tabButtons.clear();
    for (const tab of tabs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = tab.label;
      btn.className = 'char-tab-btn';
      btn.addEventListener('click', () => this.setActiveTab(tab.key));
      this.tabBar.appendChild(btn);
      this.tabButtons.set(tab.key, btn);
    }
    if (!tabs.some((tab) => tab.key === this.activeTab) && tabs.length > 0) {
      this.activeTab = tabs[0].key;
      this.onActiveTabChange?.(this.activeTab);
    }
    this.updateTabStyles();
  }

  private updateTabStyles(): void {
    for (const [key, btn] of this.tabButtons) {
      if (key === this.activeTab) btn.classList.add('active');
      else btn.classList.remove('active');
    }
  }

  private ensureTabRoot(tab: CodexTab): HTMLElement {
    const existing = this.tabRoots.get(tab);
    if (existing) return existing;
    const root = document.createElement('div');
    root.className = 'inventory-tab-content';
    this.tabRoots.set(tab, root);
    this.contentArea.appendChild(root);
    return root;
  }

  private handleSearchInput(tab: CodexTab, value: string): void {
    this.searchQueries.set(tab, value);
    if (this.searchRenderTimer !== null) {
      window.clearTimeout(this.searchRenderTimer);
    }
    this.searchRenderTimer = window.setTimeout(() => {
      this.searchRenderTimer = null;
      if (this.activeTab === tab) {
        this.render();
      }
    }, CodexPanel.SEARCH_DEBOUNCE_MS);
  }

  private captureActiveTabScrollTop(): void {
    const activeRoot = this.tabRoots.get(this.activeTab);
    const scrollHost = activeRoot?.querySelector<HTMLElement>(CodexPanel.LIST_SCROLL_SELECTOR) ?? null;
    if (!scrollHost) {
      return;
    }
    this.scrollTops.set(this.activeTab, scrollHost.scrollTop);
  }

  private restoreActiveTabScrollTop(): void {
    const activeRoot = this.tabRoots.get(this.activeTab);
    const scrollHost = activeRoot?.querySelector<HTMLElement>(CodexPanel.LIST_SCROLL_SELECTOR) ?? null;
    if (!scrollHost) {
      return;
    }
    scrollHost.scrollTop = this.scrollTops.get(this.activeTab) ?? 0;
  }
}
